import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import PropTypes from 'prop-types';
import React from 'react';
import { externalUrls, pageUrls } from 'globalconfig/constants/keys';
import { getFleetLocateMediaPath } from './fleetLocateMedia';
import { mediaUrls } from 'globalconfig/constants/mediaUrls';
import Message from 'globalcomponents/UI/Message/SpireonNew';

const serviceProvider = window.$environment?.SERVICE_PROVIDER;
const appLinks = externalUrls[serviceProvider] || {};

const FleetLocateRenderComponent = ({
  values,
  handleEdit,
  errors,
  isNotValid,
  validateForm,
  submitAction,
  customProps: { i18n, Styled, hasLoginApiStatus },
}) => {
  const handleSignIn = () => {
    validateForm().then((data) => submitAction(data));
  };

  return (
    <Styled.RightPanelContent>
      <Styled.RightPanelTop>
        <Styled.LogoWrapper>
          <img alt="FleetLocate by Spireon" src={getFleetLocateMediaPath(mediaUrls.fleetLocateLoginLogo)} />
        </Styled.LogoWrapper>

        {hasLoginApiStatus?.errorMessage && (
          <Styled.LoginError>
            <Message type="error" message={hasLoginApiStatus.errorMessage} />
          </Styled.LoginError>
        )}

        <Styled.LoginForm className="loginForm">
          <TextField
            fullWidth
            margin="normal"
            variant="standard"
            name="username"
            label={i18n.t('Username')}
            autoComplete="username"
            value={values.username || ''}
            onChange={(event) =>
              handleEdit(event.target.value, { field: 'username', type: 'username' })
            }
            error={Boolean(errors.username && !isNotValid)}
            helperText={errors.username && !isNotValid ? errors.username : ''}
            InputLabelProps={{
              shrink: true,
              style: { color: "#00000099" },
            }}
          />

          <TextField
            fullWidth
            margin="normal"
            variant="standard"
            name="password"
            label={i18n.t('common.password')}
            type="password"
            autoComplete="current-password"
            value={values.password || ''}
            onChange={(event) =>
              handleEdit(event.target.value, { field: 'password', type: 'password' })
            }
            error={Boolean(errors.password && !isNotValid)}
            helperText={errors.password && !isNotValid ? errors.password : ''}
            InputLabelProps={{
              shrink: true,
              style: { color: '#00000099' },
            }}
          />

          <Grid container alignItems="center" className="remember-forgot-row">
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="default"
                    className="remember-me-checkbox"
                    icon={<CheckBoxOutlineBlankIcon className="remember-me-checkbox-icon" />}
                    checkedIcon={<CheckBoxIcon className="remember-me-checkbox-icon remember-me-checkbox-icon--checked" />}
                    checked={values.rememberMe}
                    onChange={(event) =>
                      handleEdit(event.target.checked, { field: 'rememberMe', type: 'checkbox' })
                    }
                  />
                }
                label={i18n.t('Remember me')}
              />
            </Grid>
            <Grid item xs={6} className="forgot-password-cell">
              <Link href={pageUrls.recovery} className="forgot-password-link" id="goToForgotPasswordLink">
                {i18n.t('Forgot password?')}
              </Link>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={12}>
              <Button
                type="button"
                variant="contained"
                color="primary"
                fullWidth
                disableElevation
                className="sign-in-button"
                disabled={hasLoginApiStatus?.isLoading}
                onClick={handleSignIn}
              >
                {hasLoginApiStatus?.isLoading ? (
                  <CircularProgress color="inherit" size={24} />
                ) : (
                  'Sign In'
                )}
              </Button>
            </Grid>
          </Grid>
        </Styled.LoginForm>

        <Styled.AppStoreRow>
          {appLinks.appStore && (
            <a href={appLinks.appStore} target="_blank" rel="noopener noreferrer">
              <img src={getFleetLocateMediaPath(mediaUrls.fleetlocateAppStore)} alt="Download on the App Store" />
            </a>
          )}
          {appLinks.googlePlay && (
            <a href={appLinks.googlePlay} target="_blank" rel="noopener noreferrer">
              <img src={getFleetLocateMediaPath(mediaUrls.fleetlocateGooglePlay)} alt="Get it on Google Play" />
            </a>
          )}
        </Styled.AppStoreRow>
      </Styled.RightPanelTop>

      <Styled.RightPanelBottom>
        <Styled.LoginFooter>
          <p>
            For support, call{' '}
            <a href="tel:+18778190015" style={{textDecoration: 'none'}}>(877) 819-0015</a> or email{' '}
            <a href="mailto:fleet_support@spireon.com" style={{textDecoration: 'underline'}}>fleet_support@spireon.com</a>
          </p>
          <p className="access-code-prompt">Already have an access code?</p>
          <p className="access-code-link">
            <a href="https://auth-service-stage.spireon.com/auth/confirmForgot?clientId=fleetWeb" style={{textDecoration: 'underline'}} target="_blank" rel="noopener noreferrer">
              You can enter it here.
            </a>
          </p>
          <div className="footer-legal-links">
            <a className="footer-legal-link" href={"https://fleetlocate-stage.spireon.com/policy/privacy"}>
              Privacy Notice
            </a>
            <a className="footer-legal-link" href="https://staging.gorillasafety.com/auth/legal">
              Cookie Preferences
            </a>
          </div>
        </Styled.LoginFooter>
      </Styled.RightPanelBottom>
    </Styled.RightPanelContent>
  );
};

FleetLocateRenderComponent.propTypes = {
  values: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  isNotValid: PropTypes.bool.isRequired,
  validateForm: PropTypes.func.isRequired,
  submitAction: PropTypes.func.isRequired,
  customProps: PropTypes.shape({
    i18n: PropTypes.object.isRequired,
    Styled: PropTypes.object.isRequired,
    hasLoginApiStatus: PropTypes.object,
  }).isRequired,
};

export default FleetLocateRenderComponent;
